import { useMutation, useSuspenseQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Cross1Icon } from '@radix-ui/react-icons';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { TooltipLabel } from '@eluve/blocks';
import {
  Box,
  Button,
  Card,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  EluveCardContent,
  EluveCardHeader,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HStack,
  Input,
  NewButton,
  P,
  PageTitle,
  useToast,
} from '@eluve/components';
import { LocationBillingCodes } from '@eluve/frontend-feature-appointment';
import {
  LocationUsers,
  getLocationPageLayoutQuery,
  locationUsersFragment,
  tenantUsersFragment,
  updateLocationDetailsMutation,
} from '@eluve/frontend-feature-location-hierarchy';
import { graphql } from '@eluve/graphql.tada';
import {
  FeatureFlaggedComponent,
  useUploadGenericFile,
} from '@eluve/smart-blocks';

import { NewEhrForm } from './NewEhrForm';

const locationDetailsFormSchema = z.object({
  logo: z.instanceof(File).optional(),
  isLogoUpdated: z.boolean().default(false),
  address: z.string().optional(),
  phoneNumber: z.string().optional(),
  externalEhrId: z.string().optional(),
  externalId: z.string().optional(),
  businessName: z.string().optional(),
  placeOfServiceCode: z.string().optional(),
  taxId: z.string().optional(),
  replyToEmail: z
    .string()
    .email()
    .optional()
    .or(z.literal('').transform(() => undefined)),
});

type LocationDetailsForm = z.infer<typeof locationDetailsFormSchema>;

const locationPageQuery = graphql(
  `
    query GetLocationPage($tenantId: uuid!, $locationId: String!) {
      tenantsByPk(id: $tenantId) {
        __typename
        id
        ...TenantUsers
      }
      locationsByPk(tenantId: $tenantId, id: $locationId) {
        __typename
        id
        ...LocationUsers
      }
    }
  `,
  [locationUsersFragment, tenantUsersFragment],
);

export const LocationPage: React.FC = () => {
  const { tenantId, locationId } = useParams() as {
    tenantId: string;
    locationId: string;
  };
  const toast = useToast();

  const { data } = useSuspenseQuery(getLocationPageLayoutQuery, {
    variables: {
      locationId,
      tenantId,
    },
  });

  useSuspenseQuery(locationPageQuery, {
    variables: {
      tenantId,
      locationId,
    },
  });

  const { uploadGenericFileToGCS } = useUploadGenericFile(tenantId);

  const location = data.locationsByPk;
  const tenantExternalEhrs = data?.tenantsByPk?.externalEhrs ?? [];

  const locationDetailsFormDefaultValues: Partial<
    LocationDetailsForm & { logoUrl: string }
  > = {
    logoUrl: location?.logoUrl ?? '',
    isLogoUpdated: false,
    phoneNumber: location?.phoneNumber ?? '',
    address: location?.address ?? '',
    externalEhrId: location?.externalEhrId ?? '',
    externalId: location?.externalId ?? '',
    businessName: location?.businessName ?? '',
    placeOfServiceCode: location?.placeOfServiceCode ?? '',
    taxId: location?.taxId ?? '',
    replyToEmail: location?.replyToEmail ?? '',
  };

  const locationDetailsForm = useForm<LocationDetailsForm>({
    resolver: zodResolver(locationDetailsFormSchema),
    defaultValues: locationDetailsFormDefaultValues,
    values: {
      externalEhrId: location?.externalEhrId ?? '',
      isLogoUpdated: false,
      address: location?.address ?? '',
      externalId: location?.externalId ?? '',
      replyToEmail: location?.replyToEmail ?? '',
    },
  });

  const isLogoUpdated = locationDetailsForm.watch('isLogoUpdated');
  const logoFile = locationDetailsForm.watch('logo');

  const effectiveLogoUrl = useMemo(() => {
    if (isLogoUpdated) {
      return logoFile ? URL.createObjectURL(logoFile) : '';
    }
    return location?.logoUrl ?? '';
  }, [isLogoUpdated, location?.logoUrl, logoFile]);

  const [updateLocationDetails] = useMutation(updateLocationDetailsMutation, {
    onCompleted: () => {
      toast.success('Location details updated');
    },
    onError: () => {
      toast.error('Failed to update location details');
    },
  });

  const [createNewEhrFormOpen, setCreateNewEhrFormOpen] =
    useState<boolean>(false);

  const onSubmitLocationDetails = async (data: LocationDetailsForm) => {
    try {
      let logoUrl = location?.logoUrl ?? '';
      if (data.isLogoUpdated) {
        const { logo } = data;
        if (!logo) {
          logoUrl = '';
        } else {
          const fileExt = logo.name.split('.').pop();
          if (!fileExt) {
            toast.error('Invalid file extension.');
            return;
          }
          const result = await uploadGenericFileToGCS({
            file: logo,
            mimeType: 'image/*',
            fileExt,
          });
          if (result.url) {
            logoUrl = result.url;
          }
        }
      }

      await updateLocationDetails({
        variables: {
          locationId,
          tenantId,
          logoUrl,
          address: data.address ?? '',
          phoneNumber: data.phoneNumber ?? '',
          // We don't want to pass an empty string here since its for a foreign key
          externalEhrId: data.externalEhrId || null,
          externalId: data.externalId ?? null,
          businessName: data.businessName ?? null,
          placeOfServiceCode: data.placeOfServiceCode ?? null,
          taxId: data.taxId ?? null,
          replyToEmail: data.replyToEmail ?? null,
        },
      });

      locationDetailsForm.reset(undefined, { keepValues: true });
    } catch {
      toast.error('Failed to update location details.');
    }
  };

  if (!location?.id) {
    return <Navigate to="/not-found" />;
  }

  return (
    <div className="container pb-11">
      <div className="grid gap-5">
        <PageTitle>{location?.name}</PageTitle>
      </div>

      <NewEhrForm
        onOpenChange={setCreateNewEhrFormOpen}
        open={createNewEhrFormOpen}
      />
      <div className="grid gap-5">
        <Card className="w-full rounded-xl border-border-primary">
          <EluveCardHeader>
            <div>
              <P>Location Details</P>
            </div>
          </EluveCardHeader>
          <EluveCardContent>
            <Form {...locationDetailsForm}>
              <form
                className="flex max-w-md flex-col gap-4"
                onSubmit={locationDetailsForm.handleSubmit(
                  onSubmitLocationDetails,
                )}
              >
                <FormField
                  control={locationDetailsForm.control}
                  name="externalEhrId"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>External EHR</FormLabel>
                      <div className="flex flex-row">
                        {Boolean(tenantExternalEhrs.length) && (
                          <>
                            <div className="mr-2 w-80 rounded-lg border bg-white">
                              <Combobox>
                                <ComboboxSelectButton className="w-full border-transparent hover:bg-bg-quaternary">
                                  <Box hStack className="truncate text-sm">
                                    {(() => {
                                      const selectEhr = tenantExternalEhrs.find(
                                        (ehr) => ehr.id === field.value,
                                      );
                                      return selectEhr
                                        ? `${selectEhr.vendor} - ${selectEhr.domain}`
                                        : 'Select EHR';
                                    })()}
                                  </Box>
                                </ComboboxSelectButton>
                                <ComboboxDropdown searchPlaceholder="Select EHR">
                                  {tenantExternalEhrs.map((ehr) => (
                                    <ComboboxOption
                                      key={ehr.id}
                                      onSelect={() => field.onChange(ehr.id)}
                                    >
                                      <ComboboxSelectCheck
                                        className="mr-2"
                                        selected={field.value === ehr.id}
                                      />
                                      {`${ehr.vendor} - ${ehr.domain}`}
                                    </ComboboxOption>
                                  ))}
                                </ComboboxDropdown>
                              </Combobox>
                            </div>
                            <div className="mr-2 flex w-5 self-center">
                              <TooltipLabel label="Remove EHR from location">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="hover:bg-transparent hover:text-bg-accent-strong-primary"
                                  type="button"
                                  onClick={() => {
                                    field.onChange('');
                                  }}
                                >
                                  <Cross1Icon />
                                </Button>
                              </TooltipLabel>
                            </div>
                            <div className="mr-3 flex self-center">
                              <P className="italic">OR</P>
                            </div>
                          </>
                        )}
                        <div className="flex self-center">
                          <Button
                            type="button"
                            variant="default"
                            onClick={() => setCreateNewEhrFormOpen(true)}
                          >
                            Create New EHR
                          </Button>
                        </div>
                      </div>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="externalId"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>External ID</FormLabel>
                      <FormControl>
                        <Input placeholder="External ID" {...field} />
                      </FormControl>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="logo"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <label htmlFor="logo-url" className="sr-only">
                        Logo
                      </label>
                      <FormLabel>Logo</FormLabel>
                      <FormControl>
                        {effectiveLogoUrl ? (
                          <HStack
                            className="rounded-lg border border-border-primary bg-bg-primary p-4"
                            gap={4}
                            justify="between"
                          >
                            <img
                              alt="Logo"
                              className="max-h-20 min-w-0"
                              src={effectiveLogoUrl}
                            />
                            <NewButton
                              icon={{ name: 'Trash' }}
                              onClick={() => {
                                field.onChange(undefined);
                                locationDetailsForm.setValue(
                                  'isLogoUpdated',
                                  true,
                                );
                              }}
                              type="outline"
                            />
                          </HStack>
                        ) : (
                          <Input
                            type="file"
                            className="w-72 rounded-lg border border-border-secondary bg-white"
                            onChange={async (e) => {
                              if (e.currentTarget.files?.length) {
                                const currentFile = e.currentTarget.files[0];
                                field.onChange(currentFile);
                                locationDetailsForm.setValue(
                                  'isLogoUpdated',
                                  true,
                                );
                              }
                            }}
                          />
                        )}
                      </FormControl>
                      <FormDescription>
                        Note: this will be inherited by descendants if they
                        don't have it set.
                      </FormDescription>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="businessName"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Business Name" {...field} />
                      </FormControl>
                      <FormDescription>
                        Note: this will be inherited by descendants if they
                        don't have it set.
                      </FormDescription>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="address"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input placeholder="Address" {...field} />
                      </FormControl>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="phoneNumber"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="Phone Number" {...field} />
                      </FormControl>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="placeOfServiceCode"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Place of Service (POS) Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Place of Service (POS) Code"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Note: this will be inherited by descendants if they
                        don't have it set.
                      </FormDescription>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="taxId"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Tax ID</FormLabel>
                      <FormControl>
                        <Input placeholder="Tax ID" {...field} />
                      </FormControl>
                      <FormDescription>
                        Note: this will be inherited by descendants if they
                        don't have it set.
                      </FormDescription>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={locationDetailsForm.control}
                  name="replyToEmail"
                  render={({ field, fieldState: { error } }) => (
                    <FormItem>
                      <FormLabel>Reply-to Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Reply-to Email"
                          {...field}
                          value={field.value}
                        />
                      </FormControl>
                      <FormDescription>
                        Note: this will be inherited by descendants if they
                        don't have it set.
                      </FormDescription>
                      {error && (
                        <FormMessage className="mt-4">
                          {error.message}
                        </FormMessage>
                      )}
                    </FormItem>
                  )}
                />
                <Button
                  className="w-fit"
                  disabled={!locationDetailsForm.formState.isDirty}
                >
                  Save
                </Button>
              </form>
            </Form>
          </EluveCardContent>
        </Card>
        <Card className="w-full">
          <EluveCardHeader>
            <P>Users</P>
          </EluveCardHeader>
          <EluveCardContent>
            <LocationUsers locationId={locationId} tenantId={tenantId} />
          </EluveCardContent>
        </Card>
        <FeatureFlaggedComponent flag="BILLING_CODE_PRICES">
          <Card className="w-full">
            <EluveCardHeader>
              <P>Billing Codes</P>
            </EluveCardHeader>
            <EluveCardContent>
              <LocationBillingCodes
                locationId={locationId}
                tenantId={tenantId}
              />
            </EluveCardContent>
          </Card>
        </FeatureFlaggedComponent>
      </div>
    </div>
  );
};
